import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import LeadPopup from "@/components/LeadPopup";
import Home from "@/pages/Home";
import { track } from "@/lib/analytics";
import ServicePage from "@/pages/ServicePage";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import IndustryPage from "@/pages/IndustryPage";
import MarketPage from "@/pages/MarketPage";
import Reviews from "@/pages/Reviews";
import Legal from "@/pages/Legal";
import LocationPage from "@/pages/LocationPage";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import Services from "@/pages/Services";
import SEO from "@/components/SEO";

const NotFound = () => (
  <main className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-24 text-center" data-testid="not-found-page">
    <SEO title="Page not found" description="This page doesn't exist. Browse Grotech Web's website development, SEO, Shopify and AI services." path="/404" noindex />
    <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">404</p>
    <h1 className="mt-4 font-display text-4xl font-black tracking-tight sm:text-5xl">This page moved on.</h1>
    <p className="mt-3 max-w-sm text-sm text-white/50">The page you're looking for doesn't exist — but your next website could.</p>
    <Link
      to="/"
      data-testid="not-found-home-link"
      className="mt-8 rounded-full bg-ember px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider transition-colors hover:bg-ember-hover"
    >
      Back to Home
    </Link>
  </main>
);

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const ScrollDepthTracker = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const fired = new Set();
    const onScroll = () => {
      const h = document.documentElement;
      const pct = (h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100;
      [50, 75, 90].forEach((mark) => {
        if (pct >= mark && !fired.has(mark)) {
          fired.add(mark);
          track(`scroll_${mark}`, { page: pathname });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollManager />
      <ScrollDepthTracker />
      <div className="min-h-screen bg-obsidian text-white">
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/business-website-development" element={<ServicePage key="business-website-development" />} />
          <Route path="/b2b-lead-generation-websites" element={<ServicePage key="b2b-lead-generation-websites" />} />
          <Route path="/seo-friendly-websites" element={<ServicePage key="seo-friendly-websites" />} />
          <Route path="/local-seo-websites" element={<ServicePage key="local-seo-websites" />} />
          <Route path="/website-redesign" element={<ServicePage key="website-redesign" />} />
          <Route path="/landing-page-design" element={<ServicePage key="landing-page-design" />} />
          <Route path="/monthly-seo-growth" element={<ServicePage key="monthly-seo-growth" />} />
          <Route path="/website-development" element={<ServicePage key="website-development" />} />
          <Route path="/website-design" element={<ServicePage key="website-design" />} />
          <Route path="/lead-generation-websites" element={<ServicePage key="lead-generation-websites" />} />
          <Route path="/b2b-website-development" element={<ServicePage key="b2b-website-development" />} />
          <Route path="/seo-services" element={<ServicePage key="seo-services" />} />
          <Route path="/shopify-website-development" element={<ServicePage key="shopify-website-development" />} />
          <Route path="/shopify-seo" element={<ServicePage key="shopify-seo" />} />
          <Route path="/ai-chatbot-development" element={<ServicePage key="ai-chatbot-development" />} />
          <Route path="/ai-business-automation" element={<ServicePage key="ai-business-automation" />} />
          <Route path="/ecommerce-website-development" element={<ServicePage key="ecommerce-website-development" />} />
          <Route path="/website-development-mumbai" element={<LocationPage key="website-development-mumbai" />} />
          <Route path="/website-development-delhi" element={<LocationPage key="website-development-delhi" />} />
          <Route path="/website-development-noida" element={<LocationPage key="website-development-noida" />} />
          <Route path="/website-development-gurgaon" element={<LocationPage key="website-development-gurgaon" />} />
          <Route path="/website-development-for-manufacturers" element={<IndustryPage key="manufacturers" />} />
          <Route path="/website-development-for-consultants" element={<IndustryPage key="consultants" />} />
          <Route path="/website-development-for-real-estate" element={<IndustryPage key="real-estate" />} />
          <Route path="/website-development-for-startups" element={<IndustryPage key="startups" />} />
          <Route path="/website-development-for-small-business" element={<IndustryPage key="small-business" />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/privacy-policy" element={<Legal page="privacy-policy" />} />
          <Route path="/terms" element={<Legal page="terms" />} />
          <Route path="/website-development-usa" element={<MarketPage key="website-development-usa" />} />
          <Route path="/website-development-india" element={<MarketPage key="website-development-india" />} />
          <Route path="/seo-services-usa" element={<MarketPage key="seo-services-usa" />} />
          <Route path="/seo-services-india" element={<MarketPage key="seo-services-india" />} />
          <Route path="/shopify-development-usa" element={<MarketPage key="shopify-development-usa" />} />
          <Route path="/shopify-development-india" element={<MarketPage key="shopify-development-india" />} />
          <Route path="/ai-chatbot-development-usa" element={<MarketPage key="ai-chatbot-development-usa" />} />
          <Route path="/ai-chatbot-development-india" element={<MarketPage key="ai-chatbot-development-india" />} />
          <Route path="/ai-automation-usa" element={<MarketPage key="ai-automation-usa" />} />
          <Route path="/ai-automation-india" element={<MarketPage key="ai-automation-india" />} />
          <Route path="/website-development-dubai" element={<MarketPage key="website-development-dubai" />} />
          <Route path="/seo-services-dubai" element={<MarketPage key="seo-services-dubai" />} />
          <Route path="/shopify-development-dubai" element={<MarketPage key="shopify-development-dubai" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer />
        <WhatsAppFloat />
        <LeadPopup />
      </div>
    </BrowserRouter>
  );
}

export default App;
